/**
 * Connection freshness store (sleep/wake freshness gate, proposal 23).
 *
 * Single source of truth for the "is the live feed trustworthy right now?" signal.
 * The wake watchdog flips this to `stale` on resume, the recycle → snapshot-resync
 * path drives it `stale → resyncing → live`, and the UI (freshness banner +
 * per-row dimming) reads it. Per-symbol `lastAppliedTickTime` powers row-level
 * staleness and is kept in sync with the monotonic tick gate in the stream.
 */

import { create } from "zustand";

export type ConnectionFreshness = "live" | "stale" | "resyncing";

interface ConnectionFreshnessState {
  freshness: ConnectionFreshness;
  /** Epoch ms when the session was last marked stale (start of the suspect window). */
  staleAt: number | null;
  /** Per-symbol epoch ms of the last quote the tick gate actually applied. */
  lastAppliedTickTime: Record<string, number>;

  markStale: () => void;
  markResyncing: () => void;
  markLive: () => void;
  /** Record the applied-tick time for a symbol (called by the monotonic tick gate). */
  noteAppliedTick: (symbol: string, epochMs: number) => void;
  reset: () => void;
}

export const useConnectionFreshnessStore = create<ConnectionFreshnessState>(
  (set) => ({
    freshness: "live",
    staleAt: null,
    lastAppliedTickTime: {},

    markStale: () =>
      set((state) =>
        state.freshness === "stale"
          ? state
          : { freshness: "stale", staleAt: Date.now() }
      ),
    markResyncing: () =>
      set((state) =>
        state.freshness === "resyncing" ? state : { freshness: "resyncing" }
      ),
    markLive: () =>
      set((state) =>
        state.freshness === "live"
          ? state
          : { freshness: "live", staleAt: null }
      ),
    noteAppliedTick: (symbol, epochMs) =>
      set((state) => {
        const applied = {
          lastAppliedTickTime: {
            ...state.lastAppliedTickTime,
            [symbol]: epochMs,
          },
        };
        // Self-heal: an applied tick IS the proof the feed is trustworthy. If the
        // wake recovery died half-way (recycle threw, banner left on STALE) but
        // SignalR's own reconnect restored the stream, quotes tick while the
        // banner still screams "frozen" — forever, because only the recovery
        // path ever called markLive. Ticks applied well after staleAt (past the
        // resume burst of buffered pre-sleep frames) flip the session back to
        // live. Resyncing is left alone: that path ends in its own markLive.
        if (
          state.freshness === "stale" &&
          state.staleAt !== null &&
          Date.now() - state.staleAt > 5000
        ) {
          return { ...applied, freshness: "live" as const, staleAt: null };
        }
        return applied;
      }),
    reset: () =>
      set({ freshness: "live", staleAt: null, lastAppliedTickTime: {} }),
  })
);

/** Selector: is the whole session currently stale (frozen last-good)? */
export const selectIsStale = (s: ConnectionFreshnessState): boolean =>
  s.freshness !== "live";
